import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowUp, ArrowDown, Archive, RotateCcw, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';

const EMPTY = { name: '', description: '', org_wide: true, department_ids: [], positions: [], expires: false };

const ReqForm = ({ t, meta, initial, onDone, onCancel }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k, v) => setF((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((i) => i !== v) : [...x[k], v] }));

  const save = async () => {
    if (!f.org_wide && !f.department_ids.length && !f.positions.length) {
      toast.error(t('wr.config.scopeRequired')); return;
    }
    setBusy(true);
    try {
      if (f.id) await api.put(`/wr/requirements/${f.id}`, f);
      else await api.post('/wr/requirements', f);
      toast.success(t('wr.config.saved'));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 lg:p-8 border-primary/30 space-y-5" data-testid="wr-req-form">
      <h3 className="text-lg font-semibold">{f.id ? t('wr.config.editTitle') : t('wr.config.newTitle')}</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-sm">{t('wr.config.name')}</Label>
          <Input className="md:text-base" value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="wr-req-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('wr.config.description')}</Label>
          <Input className="md:text-base" value={f.description || ''} onChange={(e) => set('description', e.target.value)} data-testid="wr-req-desc" />
        </div>
      </div>
      <div className="flex flex-wrap gap-8">
        <div className="flex items-center gap-3">
          <Switch checked={f.org_wide} onCheckedChange={(v) => set('org_wide', v)} data-testid="wr-req-orgwide" />
          <Label className="text-base font-normal">{t('wr.config.orgWide')}</Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={f.expires} onCheckedChange={(v) => set('expires', v)} data-testid="wr-req-expires" />
          <Label className="text-base font-normal">{t('wr.config.mayExpire')}</Label>
        </div>
      </div>
      {!f.org_wide && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="wr-req-scope">
          <div className="space-y-2">
            <Label className="text-sm">{t('wr.config.departments')}</Label>
            <p className="text-sm text-muted-foreground">{t('wr.config.scopeHint')}</p>
            <div className="rounded-lg border p-3 max-h-52 overflow-y-auto space-y-2">
              {(meta?.departments || []).map((d) => (
                <label key={d.id} className="flex items-center gap-2.5 text-base cursor-pointer">
                  <Checkbox checked={f.department_ids.includes(d.id)} onCheckedChange={() => toggle('department_ids', d.id)}
                            data-testid={`wr-req-dept-${d.id}`} />
                  {d.name}
                </label>
              ))}
              {!(meta?.departments || []).length && <p className="text-sm text-muted-foreground">—</p>}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-sm">{t('wr.config.positions')}</Label>
            <p className="text-sm text-muted-foreground">{t('wr.config.positionsHint')}</p>
            <div className="rounded-lg border p-3 max-h-52 overflow-y-auto space-y-2">
              {(meta?.positions || []).map((p) => (
                <label key={p} className="flex items-center gap-2.5 text-base cursor-pointer">
                  <Checkbox checked={f.positions.includes(p)} onCheckedChange={() => toggle('positions', p)}
                            data-testid={`wr-req-pos-${p}`} />
                  {p}
                </label>
              ))}
              {!(meta?.positions || []).length && <p className="text-sm text-muted-foreground">—</p>}
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-2">
        <Button className="rounded-full" onClick={save} disabled={busy || f.name.trim().length < 2} data-testid="wr-req-save">
          {t('wr.config.save')}
        </Button>
        <Button variant="outline" className="rounded-full" onClick={onCancel} data-testid="wr-req-cancel">
          {t('wr.config.cancel')}
        </Button>
      </div>
    </Card>
  );
};

export const WrConfig = ({ t, onChanged }) => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null); // EMPTY | requirement | null
  const [days, setDays] = useState(30);

  const load = async () => {
    const [r, m, s] = await Promise.all([
      api.get('/wr/requirements?include_inactive=true'),
      api.get('/wr/meta'),
      api.get('/wr/settings'),
    ]);
    setItems(r.data.items || []);
    setMeta(m.data);
    setDays(s.data.expiring_soon_days || 30);
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e))); }, []); // eslint-disable-line

  const active = items.filter((i) => i.active);
  const archived = items.filter((i) => !i.active);

  const setActive = async (r, val) => {
    try {
      await api.post(`/wr/requirements/${r.id}/active`, { active: val });
      toast.success(t(val ? 'wr.config.reactivated' : 'wr.config.deactivated'));
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (idx, dir) => {
    const ids = active.map((r) => r.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    try { await api.put('/wr/requirements-order', { ids }); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDays = async () => {
    try {
      await api.put('/wr/settings', { expiring_soon_days: Number(days) });
      toast.success(t('wr.config.saved')); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const scopeLabel = (r) => {
    const s = r.scope || {};
    if (s.org_wide) return t('wr.config.orgWide');
    const parts = [];
    if (s.department_ids?.length) parts.push(`${t('wr.config.departments')}: ${s.department_ids.map((id) => meta?.departments?.find((d) => d.id === id)?.name || id).join(', ')}`);
    if (s.positions?.length) parts.push(`${t('wr.config.positions')}: ${s.positions.join(', ')}`);
    return parts.join(' + ');
  };

  return (
    <div className="space-y-6" data-testid="wr-config">
      <Card className="p-6 flex flex-wrap items-end gap-4" data-testid="wr-settings-card">
        <div className="space-y-1.5">
          <Label className="text-sm">{t('wr.config.thresholdLabel')}</Label>
          <Input type="number" min={1} max={365} className="w-32 md:text-base" value={days}
                 onChange={(e) => setDays(e.target.value)} data-testid="wr-threshold-input" />
        </div>
        <Button variant="outline" className="rounded-full" onClick={saveDays} data-testid="wr-threshold-save">{t('wr.config.save')}</Button>
        <p className="text-sm text-muted-foreground basis-full">{t('wr.config.thresholdHint')}</p>
      </Card>

      {editing ? (
        <ReqForm t={t} meta={meta} initial={editing.id ? {
          id: editing.id, name: editing.name, description: editing.description || '',
          org_wide: !!editing.scope?.org_wide,
          department_ids: editing.scope?.department_ids || [],
          positions: editing.scope?.positions || [], expires: !!editing.expires,
        } : EMPTY}
          onDone={async () => { setEditing(null); await load(); onChanged?.(); }}
          onCancel={() => setEditing(null)} />
      ) : (
        <Button className="rounded-full gap-2" onClick={() => setEditing(EMPTY)} data-testid="wr-add-req-btn">
          <Plus className="h-4 w-4" /> {t('wr.config.add')}
        </Button>
      )}

      <Card className="p-6 lg:p-8 space-y-4" data-testid="wr-active-reqs">
        <h3 className="text-lg font-semibold">{t('wr.config.activeTitle')} ({active.length})</h3>
        {active.length === 0 && <p className="text-base text-muted-foreground">{t('wr.config.empty')}</p>}
        {active.map((r, i) => (
          <div key={r.id} className="flex items-center gap-3 rounded-xl border px-4 py-3" data-testid={`wr-req-row-${r.id}`}>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`wr-req-up-${r.id}`}><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === active.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30" data-testid={`wr-req-down-${r.id}`}><ArrowDown className="h-4 w-4" /></button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium">{r.name}
                {r.expires && <Badge variant="outline" className="ml-2 text-xs bg-orange-50 text-orange-700 border-orange-200">{t('wr.config.mayExpire')}</Badge>}
              </p>
              <p className="text-sm text-muted-foreground truncate">{scopeLabel(r)}</p>
            </div>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setEditing(r)} data-testid={`wr-req-edit-${r.id}`}>
              <Pencil className="h-4 w-4" /> {t('wr.config.edit')}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => setActive(r, false)} data-testid={`wr-req-archive-${r.id}`}>
              <Archive className="h-4 w-4" /> {t('wr.config.deactivate')}
            </Button>
          </div>
        ))}
      </Card>

      <Card className="p-6 lg:p-8 space-y-4" data-testid="wr-archived-reqs">
        <div>
          <h3 className="text-lg font-semibold">{t('wr.config.archivedTitle')} ({archived.length})</h3>
          <p className="text-sm text-muted-foreground mt-1">{t('wr.config.archivedHint')}</p>
        </div>
        {archived.length === 0 && <p className="text-base text-muted-foreground">—</p>}
        {archived.map((r) => (
          <div key={r.id} className="flex items-center gap-3 rounded-xl border bg-muted/30 px-4 py-3" data-testid={`wr-arch-row-${r.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-base font-medium text-muted-foreground">{r.name}</p>
              <p className="text-sm text-muted-foreground truncate">{scopeLabel(r)}</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5 rounded-full" onClick={() => setActive(r, true)} data-testid={`wr-req-reactivate-${r.id}`}>
              <RotateCcw className="h-4 w-4" /> {t('wr.config.reactivate')}
            </Button>
          </div>
        ))}
      </Card>
    </div>
  );
};
